import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const items = [
  "Current market value",
  "Historical sale prices",
  "Fleet utilisation",
  "Maintenance history",
  "Engine trends",
  "Airframe analysis",
  "Market demand",
  "Residual value",
  "Lease rates",
  "Comparable aircraft",
  "Operator history",
  "Regulatory status",
  "Technical records review",
  "Maintenance programme evaluation",
];

const cockpit = "https://images.unsplash.com/photo-1764547168984-f1eae13ef38e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBhaXJjcmFmdCUyMGNvY2twaXR8ZW58MHx8fHwxNzg0NzAzNzM2fDA&ixlib=rb-4.1.0&q=85";

export default function WhyData() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section ref={ref} data-testid="why-data-section" className="relative py-32 md:py-40 border-t border-[#0E1B2E]/10 bg-[#0A1626] text-white">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-6 relative">
          <div className="relative overflow-hidden aspect-[4/5] border border-white/10">
            <motion.img
              src={cockpit}
              alt="Aviation cockpit precision"
              style={{ y }}
              className="w-full h-[115%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1626] via-transparent to-transparent"></div>
            <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C8A97E]">Precision · 001</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/60">Aviation intelligence</span>
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 flex flex-col justify-center">
          <div className="flex items-center gap-3 text-[#C8A97E] mb-6">
            <div className="hairline w-12"></div>
            <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Why data matters</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif font-light tracking-[-0.02em] text-white text-[36px] md:text-[56px] leading-[1.02] max-w-lg"
          >
            Every recommendation supported by <span className="italic text-[#C8A97E]">research</span>.
          </motion.h2>
          <p className="mt-8 text-[#A1A1AA] text-[15px] md:text-[17px] leading-relaxed max-w-lg">
            Because our clients are not buying aircraft — they are making multi-million-dollar investment decisions.
          </p>

          <ul className="mt-10 grid grid-cols-2 gap-x-8 gap-y-1 max-w-xl">
            {items.map((it, i) => (
              <motion.li
                key={it}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.03 }}
                className="text-[13px] md:text-[14px] text-white/85 border-b border-white/5 py-2.5 flex items-center gap-3"
              >
                <span className="w-1 h-1 bg-[#C8A97E]"></span>
                {it}
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
